import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { update, map, cond, prop, hasIn, path, T, always, pipe } from 'ramda';
import ViewRef from '@/util/ViewRef';
import { useGetDashboardFiltersQuery } from '@/features/addWidgetModal/filtersApi';
import { getActiveDashboardId } from '@/store-selectors';
import { useDatasourceId } from '@/hooks';
import ColumnName from '@/util/ColumnName';
import Column from '@/util/Column';
import { useGetDashboardFiltersConfigurationQuery } from '@/features/dashboard/apiSlice';
import { UUID } from '@/types';
import { useAppDispatch } from '@/store/hooks';
import {
  updateFilter,
  removeFilter,
} from '@/features/dashboard/dashboardSlice';
import { WIDGET_FILTERS_UPDATED } from '@/actions/constants';

// eslint-disable-next-line prettier/prettier
const updateColumns = update<Nullable<ColumnName>>;

const buildColumn = (seriesId: UUID) =>
  map(
    cond([
      [
        pipe(prop('columnsBySeries'), hasIn(seriesId)),
        pipe(
          // TODO: find use cases when > 1 column name per filter.
          // TODO: infer a proper type parameterisation.
          path<any>(['columnsBySeries', seriesId, 0]),
          ColumnName.build,
        ),
      ],
      [T, always(null)],
    ]),
  );

export const useColumnFilter = (selectedSavedQuery: ViewRef) => {
  const dispatch = useAppDispatch();
  const datasourceId = useDatasourceId();
  const dashboardId = useSelector(getActiveDashboardId);

  const { data: filters } = useGetDashboardFiltersQuery(
    {
      datasourceId,
      dashboardId,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: filtersConfigurations } =
    useGetDashboardFiltersConfigurationQuery(
      {
        datasourceId,
        dashboardId,
      },
      { refetchOnMountOrArgChange: true },
    );

  const [selectedColumns, setSelectedColumns] = useState<
    Nullable<ColumnName>[]
  >([]);

  useEffect(() => {
    if (filtersConfigurations) {
      setSelectedColumns(
        buildColumn(selectedSavedQuery._series)(filtersConfigurations),
      );
    }
  }, [filtersConfigurations, selectedSavedQuery._series]);

  const selectColumn = (filterIndex: number) => (column: Column) => {
    dispatch({ type: WIDGET_FILTERS_UPDATED });
    if (filters) {
      dispatch(
        updateFilter({
          filterId: filters[filterIndex]._id,
          column: column._name,
        }),
      );
    }
    setSelectedColumns(updateColumns(filterIndex, column._name));
  };

  const removeColumn = (filterIndex: number, filterId: UUID) => () => {
    dispatch({ type: WIDGET_FILTERS_UPDATED });

    dispatch(removeFilter(filterId));
    setSelectedColumns(updateColumns(filterIndex, null));
  };

  return [filters ?? [], selectedColumns, selectColumn, removeColumn] as const;
};
